import { useCallback } from 'react';
import { add as addDuration, format as formatDate, isBefore, sub as subDuration } from 'date-fns';
import { IconButton } from 'app/agriculture/components/Button/Button';
import {
    useTimelineCursorTime,
    useTimelineDispatch,
    useTimelineInterval,
    useTimelineViewMode,
} from 'app/agriculture/AgTimeline/context/TimelineContext';
import './TimelineTimeLabel.scss';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';
import { getCursorTimeForNewInterval } from 'app/agriculture/AgTimeline/utils/getCursorTimeForNewInterval';

export function TimelineTimeLabel() {
    const cursorTime = useTimelineCursorTime();
    const throttledNow = useThrottledNow();
    const {
        intervalDuration,
        cursorTimeFormat,
        getNewVisibleInterval,
        bumpDuration,
        id: viewMode,
    } = useTimelineViewMode();
    const currentInterval = useTimelineInterval();
    const { setInterval } = useTimelineDispatch();

    const handleBack = useCallback(() => {
        // if the current interval is in the future we can't rely on cursor time being useful
        if (isBefore(throttledNow, currentInterval.start)) {
            const currentIntervalEnd =
                // in month mode, the start of the interval is not always the start of the month
                viewMode === 'Month'
                    ? subDuration(currentInterval.end, bumpDuration)
                    : currentInterval.end;
            const newIntervalEnd = subDuration(currentIntervalEnd, intervalDuration);
            const newInterval = getNewVisibleInterval(newIntervalEnd);

            setInterval(newInterval);
        } else {
            const newCursorTime = subDuration(cursorTime ?? throttledNow, intervalDuration);
            const newInterval = getNewVisibleInterval(newCursorTime);

            setInterval(newInterval, {
                cursorTime: getCursorTimeForNewInterval(newInterval, throttledNow, newCursorTime),
            });
        }
    }, [
        intervalDuration,
        setInterval,
        getNewVisibleInterval,
        cursorTime,
        throttledNow,
        currentInterval,
        viewMode,
        bumpDuration,
    ]);

    const handleForward = useCallback(() => {
        // if the current interval is in the future we can't rely on cursor time being useful
        if (isBefore(throttledNow, currentInterval.start)) {
            const currentIntervalStart =
                // in month mode, the start of the interval is not always the start of the month
                viewMode === 'Month'
                    ? addDuration(currentInterval.start, bumpDuration)
                    : currentInterval.start;
            const newIntervalStart = addDuration(currentIntervalStart, intervalDuration);
            const newInterval = getNewVisibleInterval(newIntervalStart);

            setInterval(newInterval);
        } else {
            const newCursorTime = addDuration(cursorTime ?? throttledNow, intervalDuration);
            const newInterval = getNewVisibleInterval(newCursorTime);

            setInterval(newInterval, {
                cursorTime: getCursorTimeForNewInterval(newInterval, throttledNow, newCursorTime),
            });
        }
    }, [
        intervalDuration,
        setInterval,
        getNewVisibleInterval,
        cursorTime,
        throttledNow,
        currentInterval,
        viewMode,
        bumpDuration,
    ]);

    return (
        <div className="TimelineTimeLabel">
            <IconButton
                className="TimelineTimeLabel__button"
                tooltip="Back"
                icon="back"
                onClick={handleBack}
                variant="compact"
            />
            <div className="TimelineTimeLabel__cursorTime">
                {formatDate(
                    cursorTime ?? throttledNow,
                    cursorTimeFormat(cursorTime ?? throttledNow),
                )}
            </div>
            <IconButton
                className="TimelineTimeLabel__button"
                tooltip="Forward"
                icon="goto"
                onClick={handleForward}
                variant="compact"
            />
        </div>
    );
}
